import React, { FC, useEffect, useState } from 'react';
import {
  BodyText,
  FlexCol,
  FlexDynamic,
  FlexRow,
  LinkAsButton,
  Loader,
  PaywallButton,
  PrimaryButton,
} from '@minecraft.atoms';
import { LoadingSkeleton } from '@minecraft.loading-skeleton';
import { useFormikContext } from 'formik';
import { Trans, useTranslation } from '@blocs.i18n';
import { DiscountTypeEnum } from '@minecraft.graphql-operations';
import { BillingCodes } from '../../enums/plans';
import { PlanType } from '../../usePlans';
import { UseThreeDSecureFlow } from '../../ThreeDSecureFlow';
import { PaywallFunnelEvents } from '../../usePaywallFunnelEvents';
import { AcceptTrialCheckbox, TrialPlanLegalese, TrialTermLink } from '../Legal';
import { CommercialCards } from '../CommercialCards';
import { RegistrationTestimonial } from '../RegistrationTestimonial';
import { PaymentMethodFormType, PaymentSummaryProps } from '../types';
import { shouldShowBillingStateField } from './shouldShowBillingStateField';

export type CheckoutCTAProps = {
  ctaLabel: string;
  disabled?: boolean;
  isLoading?: boolean;
  selectedPaymentOption?: unknown;
  acceptedTrialTerms: boolean;
  setAcceptedTrialTerms: (value: boolean) => void;
  selectedPlan?: PlanType;
  currentPurchase?: PaymentSummaryProps['currentPurchase'];
  countryCode?: string;
  isMobile?: boolean;
  isRegistration?: boolean;
  isLifestyleUpgradeModal?: boolean;
  purchaseableItems?: PaymentSummaryProps['purchaseableItems'];
  closeBillingPopup?: () => void;
  completePurchaseError?: string;
  addPaymentError?: string;
  threeDActionContext?: UseThreeDSecureFlow['threeDActionContext'];
  uniqueId: string;
  isPaywallBillingAddressEnabled?: boolean;
  funnelEvents?: PaywallFunnelEvents;
};

// Cardholder-name fields aren't part of the billing address — excluded from requiredCardFields below
// to get just the address subset, so "address complete" and "CTA enabled" both come from one list.
const NON_ADDRESS_REQUIRED_FIELDS = ['first_name', 'last_name'];

const getAreAllRequiredFieldsFilled = ({
  isFreePlanSelected,
  isCardlessTrial,
  acceptedTrialTerms,
  requiredCardFields,
  values,
}: {
  isFreePlanSelected: boolean;
  isCardlessTrial: boolean;
  acceptedTrialTerms: boolean;
  requiredCardFields: string[];
  values: Record<string, unknown>;
}): boolean => {
  if (isFreePlanSelected) return true;
  if (isCardlessTrial) return acceptedTrialTerms;
  return requiredCardFields.every((field) => Boolean(values?.[field]));
};

export const CheckoutCTA: FC<CheckoutCTAProps> = ({
  ctaLabel,
  disabled,
  isLoading,
  selectedPaymentOption,
  acceptedTrialTerms,
  setAcceptedTrialTerms,
  selectedPlan,
  currentPurchase,
  countryCode,
  isMobile,
  isRegistration,
  isLifestyleUpgradeModal,
  purchaseableItems,
  closeBillingPopup,
  completePurchaseError,
  addPaymentError,
  threeDActionContext,
  uniqueId,
  isPaywallBillingAddressEnabled,
  funnelEvents,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<PaymentMethodFormType>();

  const translationCode =
    selectedPlan?.translationCode ?? selectedPlan?.billingCode ?? currentPurchase?.subscription?.plan?.billingCode;
  const appliedCouponCode = currentPurchase?.coupon?.code;
  const isSelectedPlanRepRegDiscount = selectedPlan?.discountType === DiscountTypeEnum.ReppedRegistrationDiscount;
  const isFreePlanSelected = (selectedPlan?.billingCode as BillingCodes | undefined) === BillingCodes.registration;
  const isTrialPlan = selectedPlan?.isTrialPeriodEnabled && !appliedCouponCode;
  const isCardlessTrial = selectedPlan?.isCardlessTrial;
  // acceptTermsCheckboxKey is set on every plan (trial or not, see usePlans.tsx) — it's the checkbox's
  // label text, not a signal that a checkbox should show. Only an actual trial plan needs one.
  const displayTrialCheckbox = isTrialPlan && !isFreePlanSelected && !isSelectedPlanRepRegDiscount;
  const requiredCardFields = isPaywallBillingAddressEnabled
    ? [
        'first_name',
        'last_name',
        'address_line_1',
        'city',
        ...(shouldShowBillingStateField(values?.country) ? ['state_region'] : []),
        'country',
        'postal_code',
      ]
    : ['first_name', 'last_name', 'postal_code'];
  const areAllRequiredFieldsFilled = getAreAllRequiredFieldsFilled({
    isFreePlanSelected,
    isCardlessTrial,
    acceptedTrialTerms,
    requiredCardFields,
    values,
  });

  const requiredAddressFields = requiredCardFields.filter((field) => !NON_ADDRESS_REQUIRED_FIELDS.includes(field));
  const isAddressComplete =
    requiredAddressFields.length > 0 && requiredAddressFields.every((field) => Boolean(values?.[field]));
  const [wasAddressComplete, setWasAddressComplete] = useState(false);
  useEffect(() => {
    if (isAddressComplete && !wasAddressComplete) {
      funnelEvents?.trackBillingAddressCompleted();
    }
    if (isAddressComplete !== wasAddressComplete) {
      setWasAddressComplete(isAddressComplete);
    }
  }, [isAddressComplete, wasAddressComplete, funnelEvents]);
  const finalDisabled =
    disabled ||
    isLoading ||
    (isTrialPlan && !acceptedTrialTerms) ||
    (!areAllRequiredFieldsFilled && !selectedPaymentOption);

  const PurchaseButton = isLifestyleUpgradeModal ? PaywallButton : PrimaryButton;

  return (
    <FlexRow w="full" justifyContent="flex-end" gap="2">
      <FlexCol gap="1" w="full">
        <FlexDynamic
          mb="8"
          mdProps={{ mb: '0', maxH: '28', className: 'cn_overflow-visible cn_overflow-y-scroll' }}
          w="full"
          justifyContent="flex-end"
          gap="4"
          alignItems="flex-end"
        >
          <BodyText>
            {isCardlessTrial ? (
              <Trans i18nKey="common:paywall.summary.cardlessTrial.terms" components={{ a: <TrialTermLink /> }} />
            ) : (
              <Trans i18nKey="common:paywall.summary.terms" components={{ a: <TrialTermLink /> }} />
            )}
          </BodyText>
          {isTrialPlan && !isCardlessTrial && (
            <FlexCol w="full" mb={isLoading || !isMobile ? '2' : '8'}>
              <TrialPlanLegalese billingCode={translationCode} selectedPlan={selectedPlan} countryCode={countryCode} />
            </FlexCol>
          )}
          {isMobile && isSelectedPlanRepRegDiscount && <RegistrationTestimonial />}
          {isMobile && isRegistration && <CommercialCards />}
        </FlexDynamic>
        {!isLoading && <FlexDynamic w="full" mb="16" mdProps={{ mb: '0' }} />}
        <FlexDynamic
          direction="column"
          mdProps={{ className: '', mb: '0' }}
          className="cn_fixed cn_atom_px-4"
          mb="12"
          w="full"
          bottom={-3}
          left={0}
          bgColor="card"
        >
          <Loader
            loading={isLoading || Boolean(threeDActionContext?.token)}
            LoaderComponent={<LoadingSkeleton className="cn_w-full" height="3rem" />}
          >
            {displayTrialCheckbox && (
              <AcceptTrialCheckbox
                text={selectedPlan?.acceptTermsCheckboxKey}
                checked={acceptedTrialTerms}
                onChange={setAcceptedTrialTerms}
                isSticky
              />
            )}
            <FlexCol w="full" mb="4">
              <PurchaseButton
                {...(isLifestyleUpgradeModal && { color: 'gold' })}
                full
                type="submit"
                label={ctaLabel}
                disabled={finalDisabled}
                data-unique-id={uniqueId}
                data-qa-id="complete-purchase-button"
              />
              {purchaseableItems?.length > 0 && (
                <LinkAsButton
                  className="cn_w-full cn_text-center"
                  label={t('common:paywall.purchaseMedia.keepMediaLock')}
                  onClick={() => closeBillingPopup?.()}
                />
              )}
            </FlexCol>
            {addPaymentError && <BodyText color="error">{addPaymentError}</BodyText>}
            {completePurchaseError && <BodyText color="error">{completePurchaseError}</BodyText>}
          </Loader>
        </FlexDynamic>
      </FlexCol>
    </FlexRow>
  );
};
